<template>
  <div>
    <form @submit.prevent="submit">
      <h2>{{ $t('W2P_LETTER_OPTIONS_HEADER_TITLE') }}</h2>

      <div class="lo-cards">
        <input
          class="custom-radio"
          name="print"
          type="radio"
          value="single-sided"
          v-model="letterOptions.printType"
          v-validate="'required'"
          id="single-sided"
        />
        <label
          class="lo-card-container"
          :aria-label="$t('W2P_PRODUCT_DEFINITION_SINGLE-SIDED')"
          for="single-sided"
        >
          <div class="lo-img">
            <img :src="require(`@/assets/images/single-sided.svg`)" />
          </div>
          <p class="lo-card-text">
            {{ $t('W2P_PRODUCT_DEFINITION_SINGLE-SIDED') }}
          </p>
        </label>

        <input
          class="custom-radio"
          name="print"
          type="radio"
          value="double-sided"
          v-model="letterOptions.printType"
          v-validate="'required'"
          id="double-sided"
        />
        <label
          class="lo-card-container"
          :aria-label="$t('W2P_PRODUCT_DEFINITION_DOUBLE-SIDED')"
          for="double-sided"
        >
          <div class="lo-img">
            <img :src="require(`@/assets/images/double-sided.svg`)" />
          </div>
          <p class="lo-card-text">
            {{ $t('W2P_PRODUCT_DEFINITION_DOUBLE-SIDED') }}
          </p>
        </label>
      </div>

      <div
        class="lo-card-message"
        v-if="letterOptions.printType === 'single-sided'"
      >
        <p class="bold">{{ $t('W2P_LETTER_OPTIONS_SINGLESIDED_LETTER') }}</p>
        <p class="pn-blue">
          <span>{{ $t('W2P_FROM') }} </span>
          <span class="letter-price bold">{{
            $n(selectedProductBasePrice, 'currency', 'sv-SE')
          }}</span>
          <span> {{ $t('W2P_KR_EACH') }}</span>
        </p>

        <ul>
          <li>
            {{ $t('W2P_PRODUCT_DEFINITION_PDF_FORMAT_DESCRIPTION_A4') }}
          </li>
          <li>
            {{ $t('W2P_LETTER_OPTIONS_SINGLESIDED_PRINT_INFORMATION_1') }}
          </li>
          <li>
            {{ $t('W2P_LETTER_OPTIONS_SINGLESIDED_PRINT_INFORMATION_2') }}
          </li>
        </ul>
      </div>

      <div
        class="lo-card-message"
        v-else-if="letterOptions.printType === 'double-sided'"
      >
        <p class="bold">{{ $t('W2P_LETTER_OPTIONS_DOUBLESIDED_LETTER') }}</p>
        <p class="pn-blue">
          <span>{{ $t('W2P_FROM') }} </span>
          <span class="letter-price bold">{{
            $n(selectedProductBasePrice, 'currency', 'sv-SE')
          }}</span>
          <span> {{ $t('W2P_KR_EACH') }}</span>
        </p>

        <ul>
          <li>
            {{ $t('W2P_PRODUCT_DEFINITION_PDF_FORMAT_DESCRIPTION_A4') }}
          </li>
          <li>
            {{ $t('W2P_LETTER_OPTIONS_DOUBLESIDED_PRINT_INFORMATION_1') }}
          </li>
          <li>
            {{ $t('W2P_LETTER_OPTIONS_DOUBLESIDED_PRINT_INFORMATION_2') }}
          </li>
        </ul>
      </div>

      <div class="lo-check-container row">
        <div class="lo-check-box custom">
          <pn-checkbox-container
            class="lo-check-box__label"
            :aria-disabled="!selectedProduct"
            @checkchange="checkBoxChangePreface"
          >
            <input
              name="preface"
              type="checkbox"
              v-model="letterOptions.includePreface"
              :disabled="!selectedProduct"
            />
          </pn-checkbox-container>

          <div :class="['custom-label', { 'with-price': selectedProduct }]">
            <p>{{ $t('W2P_LETTER_OPTIONS_PREFACE_LABEL') }}</p>
            <p class="small bold" v-if="selectedProduct">
              + {{ $n(extraPagePrice, 'currency', 'sv-SE') }}
              {{ $t('W2P_KR_EACH') }}
            </p>
          </div>

          <CustomResponsiveTooltip
            :solid="true"
            :color="'gray400'"
            :hoverColor="'blue700'"
            :position="'right'"
            :padded="true"
          >
            {{ $t('W2P_LETTER_OPTIONS_PREFACE_TOOLTIP') }}
          </CustomResponsiveTooltip>
        </div>
      </div>

      <div class="lo-check-container row">
        <div class="lo-check-box">
          <pn-checkbox-container
            class="lo-check-box__label"
            :label="$t('W2P_LETTER_OPTIONS_PAYMENTSLIP_LABEL')"
            :aria-disabled="!selectedProduct"
            @checkchange="checkBoxChangePaymentSlip"
          >
            <input
              type="checkbox"
              name="payment-slip"
              v-model="letterOptions.includePaymentSlip"
              :disabled="!selectedProduct"
            />
          </pn-checkbox-container>

          <CustomResponsiveTooltip
            :solid="true"
            :color="'gray400'"
            :hoverColor="'blue700'"
            :position="'right'"
            :padded="true"
          >
            {{ $t('W2P_LETTER_OPTIONS_PAYMENTSLIP_TOOLTIP') }}
          </CustomResponsiveTooltip>
        </div>
      </div>

      <div class="lo-input-container row">
        <pn-input-container
          :label="$t('W2P_LETTER_OPTIONS_NAME_LABEL')"
          class="lo-input-name-container"
          :invalid="errors.has('name')"
          :noicon="errors.has('name')"
        >
          <input
            name="name"
            v-validate="'max:35'"
            v-model="letterOptions.mailoutName"
          />
          <CustomResponsiveTooltip
            :solid="true"
            :color="'gray400'"
            :hoverColor="'blue700'"
            :position="'right'"
            :padded="true"
          >
            {{ $t('W2P_LETTER_OPTIONS_NAME_TOOLTIP') }}
          </CustomResponsiveTooltip>
        </pn-input-container>
      </div>

      <!-- Cost Estimate -->
      <div class="lo-input-container">
        <div class="lo-estimate-container">
          <h2>
            {{ $t('W2P_LETTER_OPTIONS_COST_ESTIMATE_TITLE') }}
          </h2>
          <p class="small lo-estimate-description">
            {{ $t('W2P_LETTER_OPTIONS_COST_ESTIMATE_DESCRIPTION') }}
          </p>
          <div class="lo-number-cards">
            <div class="lo-number-container">
              <p
                class="bold spacing"
                v-html="$t('W2P_LETTER_OPTIONS_COST_ESTIMATE_PAGE_QUESTION')"
              ></p>
              <p class="small spacing lo-number-description">
                {{
                  $t('W2P_LETTER_OPTIONS_COST_ESTIMATE_PAGE_DESCRIPTION', {
                    max: productDelivery ? productDelivery.page_count.max : 12
                  })
                }}
              </p>
              <number-input
                :min="productDelivery ? productDelivery.page_count.base : 1"
                :max="productDelivery ? productDelivery.page_count.max : 12"
                controls
                size="custom"
                center
                v-model="letterOptions.optionalPageCount"
              />
            </div>
            <div class="lo-number-container">
              <p class="bold spacing">
                {{ $t('W2P_LETTER_OPTIONS_COST_ESTIMATE_RECIPIENTS_QUESTION') }}
              </p>
              <p class="small spacing lo-number-description">
                {{
                  $t('W2P_LETTER_OPTIONS_COST_ESTIMATE_RECIPIENTS_DESCRIPTION')
                }}
              </p>
              <number-input
                :min="productDelivery ? productDelivery.addressLimit.min : 1"
                :max="productDelivery ? productDelivery.addressLimit.max : 9999"
                controls
                size="custom"
                center
                v-model="letterOptions.optionalRecipientsCount"
              />
            </div>
          </div>
          <div class="lo-radio-container">
            <div class="lo-radio-controls" role="radiogroup">
              <div
                class="lo-radio-option"
                :class="{
                  selected: letterOptions.optionalDeliveryOption === 'economy'
                }"
                role="radio"
                :aria-checked="
                  String(letterOptions.optionalDeliveryOption === 'economy')
                "
                tabindex="0"
                @click="letterOptions.optionalDeliveryOption = 'economy'"
                @keydown.enter="
                  letterOptions.optionalDeliveryOption = 'economy'
                "
                @keydown.space.prevent="
                  letterOptions.optionalDeliveryOption = 'economy'
                "
              >
                <pn-radio-button
                  value="economy"
                  name="optional-delivery-option"
                  :checked="letterOptions.optionalDeliveryOption === 'economy'"
                ></pn-radio-button>
                <span>
                  {{ $t('W2P_LETTER_OPTIONS_COST_ESTIMATE_ECONOMY') }}
                  <span class="min-text">
                    ({{ $t('W2P_LETTER_MINIMUM_500_RECIPIENTS') }})
                  </span>
                </span>
              </div>

              <div
                class="lo-radio-option"
                :class="{
                  selected:
                    letterOptions.optionalDeliveryOption === 'first-class'
                }"
                role="radio"
                :aria-checked="
                  String(letterOptions.optionalDeliveryOption === 'first-class')
                "
                tabindex="0"
                @click="letterOptions.optionalDeliveryOption = 'first-class'"
                @keydown.enter="
                  letterOptions.optionalDeliveryOption = 'first-class'
                "
                @keydown.space.prevent="
                  letterOptions.optionalDeliveryOption = 'first-class'
                "
              >
                <pn-radio-button
                  value="first-class"
                  name="optional-delivery-option"
                  :checked="
                    letterOptions.optionalDeliveryOption === 'first-class'
                  "
                ></pn-radio-button>
                <span>
                  {{ $t('W2P_LETTER_OPTIONS_COST_ESTIMATE_FIRST_CLASS') }}
                </span>
              </div>
            </div>

            <CustomResponsiveTooltip
              :solid="true"
              :color="'gray400'"
              :hoverColor="'blue700'"
              :position="'right'"
              :padded="true"
            >
              <p
                v-html="$t('W2P_LETTER_OPTIONS_COST_ESTIMATE_ECONOMY_TOOLTIP')"
              />
              <br />
              <p
                v-html="
                  $t('W2P_LETTER_OPTIONS_COST_ESTIMATE_FIRSTCLASS_TOOLTIP')
                "
              />
              <br />
              <p v-html="$t('W2P_LETTER_OPTIONS_COST_ESTIMATE_NOTE_TOOLTIP')" />
            </CustomResponsiveTooltip>
          </div>

          <div
            class="lo-check-container row"
            v-if="letterOptions.optionalDeliveryOption === 'first-class'"
          >
            <div class="lo-check-box">
              <pn-checkbox-container
                class="lo-check-box__label"
                :label="$t('W2P_LETTER_OPTIONS_COST_ESTIMATE_LATE_PAYMENT')"
                @checkchange="checkBoxChangeLatePayment"
              >
                <input type="checkbox" name="latePayment" />
              </pn-checkbox-container>

              <CustomResponsiveTooltip
                :solid="true"
                :color="'gray400'"
                :hoverColor="'blue700'"
                :position="'right'"
                :padded="true"
              >
                {{
                  $t('W2P_LETTER_OPTIONS_COST_ESTIMATE_LATE_PAYMENT_TOOLTIP')
                }}
              </CustomResponsiveTooltip>
            </div>
          </div>
        </div>
      </div>

      <div class="lo-button-container">
        <pn-button-container secondary size="medium">
          <button type="button" @click="$router.replace('/')">
            {{ $t('W2P_LETTER_BACK') }}
          </button>
        </pn-button-container>

        <pn-button-container size="medium">
          <button class="next" :disabled="isFormInvalid" type="submit">
            {{ $t('W2P_LETTER_NEXT') }}
          </button>
        </pn-button-container>
      </div>
    </form>
  </div>
</template>

<script>
import axios from 'axios'
import { mapMutations, mapGetters, mapState } from 'vuex'
import { mapFields } from 'vee-validate'

import NumberInput from '@/components/common/NumberInput'
import CustomResponsiveTooltip from '@/components/common/CustomResponsiveTooltip'

export default {
  components: {
    NumberInput,
    CustomResponsiveTooltip
  },
  data() {
    return {
      letterOptions: {},
      letterOptionsBackup: {},
      products: []
    }
  },
  watch: {
    letterOptions: {
      handler() {
        this.checkAndUpdateForm()
      },
      deep: true
    }
  },
  computed: {
    ...mapFields(['name', 'print']),
    ...mapGetters(['isLoggedIn', 'isBusinessUser']),
    ...mapState({
      upload: state => state.letter.upload,
      cart: state => state.cart,
      address: state => state.letter.address
    }),
    service() {
      return this.letterOptions.includePaymentSlip
        ? 'paymentSlips'
        : 'information'
    },
    isFormInvalid() {
      return (
        this.errors.count() > 0 ||
        !Object.keys(this.fields).every(field => {
          return this.fields[field] && this.fields[field].valid
        })
      )
    },
    selectedProduct() {
      return this.products.find(product => {
        const productPrintType = product.singleSided
          ? 'single-sided'
          : 'double-sided'
        const productIsPaymentSlip = product.siteCategory === 'paymentSlips'

        return (
          this.letterOptions.printType === productPrintType &&
          this.letterOptions.includePaymentSlip === productIsPaymentSlip
        )
      })
    },
    selectedProductBasePrice() {
      return this.selectedProduct
        ? this.selectedProduct.pricingInfo.printingInfo.basePrice.price / 100
        : null
    },
    deliveryId() {
      let deliveryId = ''
      if (this.isBusinessUser) {
        if (this.letterOptions.optionalDeliveryOption === 'first-class') {
          if (this.letterOptions.optionalLatePayment) {
            deliveryId = '100'
          } else {
            deliveryId = '200'
          }
        } else {
          // economy
          deliveryId =
            this.letterOptions.optionalRecipientsCount < 500 ? '100' : '300'
        }
      } else {
        if (this.letterOptions.optionalLatePayment) {
          deliveryId = '400'
        } else {
          deliveryId = '500'
        }
      }
      return deliveryId
    },
    productDelivery() {
      if (!this.selectedProduct) return null

      const productDelivery = {
        format: this.selectedProduct.format,
        addressLimit: this.selectedProduct.addressLimit || {
          min: 1,
          max: 9999
        },
        page_count: Object.assign(
          { origMax: this.selectedProduct.page_count.max },
          this.selectedProduct.page_count
        ),
        productName: this.selectedProduct.name,
        productId: this.selectedProduct.productId,
        salesOrganization: this.selectedProduct.salesOrganization
      }

      if (this.letterOptions.includePreface) {
        productDelivery.page_count.max -= this.selectedProduct.singleSided
          ? 1
          : 2
      }

      return productDelivery
    },
    extraPagePrice() {
      if (!this.selectedProduct) return null

      return this.selectedProduct.pricingInfo.printingInfo.extraPage.price / 100
    }
  },
  methods: {
    ...mapMutations('letter/upload', [
      'UPDATE_LETTER_OPTIONS',
      'SET_COST_ESTIMATE',
      'SET_MATERIAL',
      'RESET_MATERIAL'
    ]),
    ...mapMutations('letter/address', ['RESET_STATE']),
    ...mapMutations('cart', ['UPDATE_PRODUCTDELIVERY', 'UPDATE_SERVICETYPE']),
    ...mapMutations({
      resetRecipients(commit) {
        return commit('letter/recipient/RESET_STATE_ALL')
      }
    }),
    getRecipientsData() {
      return {
        pageCount: this.letterOptions.optionalPageCount,
        recipientsCount: this.letterOptions.optionalRecipientsCount
      }
    },
    checkAndUpdateForm() {
      if (!this.selectedProduct) return

      const options = {
        productId: this.selectedProduct.productId,
        recipientsData: this.getRecipientsData(),
        deliveryId: this.deliveryId,
        mailoutName:
          this.letterOptions.mailoutName || this.$t(this.selectedProduct.name),
        letterOptions: this.letterOptions
      }

      this.UPDATE_LETTER_OPTIONS(options)
      this.UPDATE_PRODUCTDELIVERY(this.productDelivery)
      this.UPDATE_SERVICETYPE(this.selectedProduct.siteCategory)

      this.validateFiles()
    },
    validateFiles() {
      if (this.upload.valid) {
        const currentPages = this.upload.details.numberOfPages
          ? this.upload.details.numberOfPages.current
          : 0
        const maxPages = this.cart.productDelivery.page_count.max || 0

        if (currentPages > maxPages) {
          this.RESET_MATERIAL()

          // display warning in upload page
          this.SET_MATERIAL({
            key: 'showInvalidatedMaterialMsg',
            value: true
          })
        } else {
          this.SET_MATERIAL({
            key: 'previewUrl',
            value: ''
          })
        }
      }

      if (!!this.address.id && !!this.address.numberOfRecipients) {
        if (
          this.letterOptions.includePaymentSlip !==
          this.letterOptionsBackup.includePaymentSlip
        ) {
          this.RESET_STATE()
        }
      }
    },
    submit() {
      this.$router.push('/letter/upload/upload-letter')
      return false
    },
    checkBoxChangePreface(e) {
      this.letterOptions.includePreface = e.detail
    },
    checkBoxChangePaymentSlip(e) {
      this.letterOptions.includePaymentSlip = e.detail
    },
    checkBoxChangeLatePayment(e) {
      this.letterOptions.optionalLatePayment = e.detail
    }
  },
  created() {
    if (sessionStorage.getItem('orderPlaced')) {
      sessionStorage.removeItem('orderPlaced')
    }

    this.$store.subscribe(mutation => {
      if (mutation.type === 'letter/upload/UPDATE_LETTER_OPTIONS') {
        if (!this.upload.isCostEstimate) {
          this.resetRecipients()
        }
      }
    })

    this.SET_COST_ESTIMATE(true)

    this.letterOptions = Object.assign({}, this.upload.letterOptions)
    this.letterOptionsBackup = Object.assign({}, this.letterOptions)
    const userType = this.isBusinessUser ? 'company' : 'public'

    axios({
      method: 'post',
      url: `${process.env.VUE_APP_W2P_API_BASE_URL}/clientAPI/products/graphql`,
      data: {
        query: `
          query {
            getProducts(userType: "${userType}") {
              productId
              name
              singleSided
              siteCategory
              pricingInfo {
                printingInfo {
                  basePrice {
                    price
                  }
                  extraPage {
                    price
                  }
                }
              }
              page_count {
                max
                base
              }
              addressLimit {
                max
                min
              }
              format{
                name
                length
                width
                format
                description
                units
              }
              salesOrganization
            }
          }
        `
      }
    })
      .then(res => {
        this.products = res.data.data.getProducts
        this.checkAndUpdateForm()
      })
      .catch(err => {})
  },
  beforeDestroy() {
    this.SET_COST_ESTIMATE(false)
  }
}
</script>

<style lang="scss" scoped>
.clickable {
  cursor: pointer;
}

.lo-estimate-container {
  margin-top: 2.5em;
}

.lo-estimate-description {
  margin-top: 1em;
  color: $gray700;
}

.spacing {
  padding-bottom: 0.5em;
}

.lo-number-description {
  color: $gray700;
}

.letter-price {
  font-size: 1.5em;
}

ul {
  line-height: 1.6em;
}

.custom-radio {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.lo-img {
  display: block;
  margin: auto;
  img {
    width: 100%;
  }
}

.lo-checkbox-item {
  display: flex;
}

.lo-check-box {
  display: flex;
  width: 100%;

  &.custom {
    pn-checkbox-container {
      flex: 0;
    }

    .custom-label {
      cursor: pointer;
      font-size: 0.875em;
      flex: 1;
      line-height: 24px;

      &.with-price {
        line-height: unset;
      }
    }
  }

  &__label {
    width: 100%;
  }
}

.min-text {
  margin-left: 0.5em;
  color: $gray700;
}

.lo-cards {
  margin-top: 2em;
  display: flex;
  width: 100%;
  max-width: 70rem;

  .lo-card-container {
    border: 0.1rem solid $gray200;
    padding: 7%;
    width: 100%;
    cursor: pointer;

    &:first-of-type {
      margin-right: 1em;
    }
  }
}

input.custom-radio[type='radio']:focus + label,
input.custom-radio[type='radio']:checked + label,
.lo-card-container:hover {
  border: 0.1rem solid $blue700;
  background-color: $blue50;
}

input.custom-radio[type='radio']:checked + label {
  border-bottom: 0.7rem solid $blue700;
}

.lo-radio-controls {
  display: flex;
  flex-direction: column;
}

.lo-radio-option {
  display: flex;
  align-items: center;
  margin-bottom: 0.8em;
  cursor: pointer;
  border-radius: 0.4rem;

  &:focus {
    outline: 0.2rem solid $blue700;
    outline-offset: 0.2rem;
  }

  &:last-child {
    margin-bottom: 0;
  }

  &.selected {
    span {
      font-weight: 500;
    }
  }

  span {
    margin-left: 0.8em;
  }
}

.lo-number-cards {
  margin-top: 2em;
  display: flex;
  width: 100%;
  max-width: 70rem;

  label:first-child {
    margin-right: 1em;
  }

  div:first-child {
    margin-right: 1em;
  }

  .lo-number-container {
    border: 0.1rem solid $gray200;
    width: 100%;
    flex: 1 1 0;
    padding: 1.5em;
    text-align: center;
    display: flex;
    flex-direction: column;
  }
}

.row {
  width: 100%;

  pn-input-container {
    width: 100%;
  }
}

.lo-check-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 1.5rem;
  border: 0.1rem solid $gray200;
  padding: 1em;
}

.lo-radio-container {
  display: flex;
  flex-wrap: wrap;
  margin-top: 1.5rem;
  border: 0.1rem solid $gray200;
  padding: 1.5rem 1.5rem 0rem;
  width: 100%;
  height: 100%;
  justify-content: space-between;
}

.lo-radio-tooltip {
  justify-content: flex-end;
}

.lo-checkbox-label {
  display: flex;
  align-items: center;
}

.lo-input-container {
  margin-top: 1.5rem;
}

.lo-button-container {
  margin-top: 3.5em;
  width: 100%;
  display: flex;
  justify-content: space-between;

  pn-button-container {
    width: 70%;
    max-width: 15em;
    min-width: 0;

    &:first-child {
      margin-right: 0.5em;
    }

    &:last-child {
      margin-left: 0.5em;
    }
  }
}

.lo-card-message {
  margin-top: 1em;
}

.lo-card-text {
  margin-top: 1em;
  text-align: center;
}

.pn-blue {
  color: $blue700;
}

@media screen and (max-width: 50em) {
  .lo-number-cards {
    flex-direction: column;
    .lo-number-container:first-child {
      margin-bottom: 1em;
      margin-right: 0;
    }
  }
}
</style>
